// MindARThree bootstrap + per-card augmentation orchestration.

import * as THREE from 'three';
// MindAR types come from the npm package; binding path varies by version.
// 1.2.x exposes via 'mind-ar/dist/mindar-image-three.prod.js' (production build),
// but ESM-friendly path is 'mind-ar/dist/mindar-image-three.prod.js' — Vite handles via optimizeDeps.exclude.
// @ts-ignore — types not bundled
import { MindARThree } from 'mind-ar/dist/mindar-image-three.prod.js';
import { api, type AugmentationPublic, type CardPublic } from './api';
import { spawnElement, tickMixer, type SpawnedElement } from './augmentation';
import { TimelineRunner } from './timeline';

interface AnchorBinding {
  card: CardPublic;
  anchor: { group: THREE.Group; onTargetFound: () => void; onTargetLost: () => void };
  elements: SpawnedElement[];
  timeline: TimelineRunner | null;
  augmentation: AugmentationPublic | null;
  loaded: boolean;
}

interface InitOptions {
  container: HTMLElement;
  targetSrc: string;
  apiBase?: string;
  onStatus?: (msg: string) => void;
  onError?: (err: Error) => void;
}

export class ArController {
  private mindar: any = null;
  private listener: THREE.AudioListener | null = null;
  private bindings: AnchorBinding[] = [];
  private lastFrameTime = 0;
  private rafId: number | null = null;
  private status: (msg: string) => void = () => {};

  async init(opts: InitOptions): Promise<void> {
    this.status = opts.onStatus ?? (() => {});
    if (opts.apiBase) api.setBase(opts.apiBase);

    this.status('fetching cards…');
    const cards = await api.cards();
    if (cards.length === 0) {
      throw new Error('no cards configured in API');
    }

    this.status('initializing MindAR…');
    this.mindar = new MindARThree({
      container: opts.container,
      imageTargetSrc: opts.targetSrc,
      uiScanning: 'no',
      uiLoading: 'no',
      uiError: 'no',
      maxTrack: 1,
      filterMinCF: 0.0001,
      filterBeta: 0.001,
    });

    const { renderer, scene, camera } = this.mindar;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;

    // Lighting suitable για AR composition
    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    const directional = new THREE.DirectionalLight(0xffffff, 1.0);
    directional.position.set(0.5, 1, 0.3);
    scene.add(ambient, directional);

    this.listener = new THREE.AudioListener();
    camera.add(this.listener);

    // Wire up anchors. Vuforia field `target_name` (legacy) maps to card index in cards.mind:
    // card_1 → index 0, card_2 → index 1. Order ascending by card.id.
    const sortedCards = [...cards].sort((a, b) => a.id - b.id);
    for (let i = 0; i < sortedCards.length; i++) {
      const card = sortedCards[i];
      const anchor = this.mindar.addAnchor(i);
      const binding: AnchorBinding = {
        card,
        anchor,
        elements: [],
        timeline: null,
        augmentation: null,
        loaded: false,
      };

      // Grace-period debounce: brief tracking loss shouldn't pause augmentation.
      // User can wobble phone freely; augmentation pauses only after sustained loss.
      let lossTimeout: ReturnType<typeof setTimeout> | undefined;
      const GRACE_MS = 2000;

      anchor.onTargetFound = () => {
        if (lossTimeout !== undefined) {
          clearTimeout(lossTimeout);
          lossTimeout = undefined;
        }
        this.status(`κάρτα ${card.id} ορατή`);
        void this.ensureLoaded(binding).then(() => {
          binding.timeline?.start();
        });
      };
      anchor.onTargetLost = () => {
        if (lossTimeout !== undefined) clearTimeout(lossTimeout);
        lossTimeout = setTimeout(() => {
          this.status('αναζήτηση κάρτας…');
          binding.timeline?.pause();
          lossTimeout = undefined;
        }, GRACE_MS);
      };

      this.bindings.push(binding);
    }

    this.status('εκκίνηση κάμερας…');
    await this.mindar.start();

    this.startRenderLoop();
    this.status('έτοιμο — σαρώστε μια κάρτα');
  }

  private async ensureLoaded(binding: AnchorBinding): Promise<void> {
    if (binding.loaded) return;
    if (!binding.card.active_augmentation_id) {
      this.status(`κάρτα ${binding.card.id}: δεν υπάρχει ενεργή επαύξηση`);
      binding.loaded = true;
      return;
    }
    this.status(`φόρτωση επαύξησης κάρτας ${binding.card.id}…`);
    const aug = await api.augmentation(binding.card.active_augmentation_id);
    binding.augmentation = aug;

    if (!this.listener) throw new Error('audio listener not ready');
    const spawned: SpawnedElement[] = [];
    for (const el of aug.elements) {
      try {
        const s = await spawnElement(el, this.listener);
        binding.anchor.group.add(s.root);
        s.setVisibility(false, 0);
        spawned.push(s);
      } catch (err) {
        console.warn('[ar] element spawn failed', el, err);
      }
    }
    binding.elements = spawned;
    binding.timeline = new TimelineRunner(spawned, aug.augmentation.total_duration_ms);
    binding.loaded = true;
  }

  private startRenderLoop(): void {
    const { renderer, scene, camera } = this.mindar;
    this.lastFrameTime = performance.now();

    const loop = () => {
      const now = performance.now();
      const dt = (now - this.lastFrameTime) / 1000;
      this.lastFrameTime = now;

      for (const b of this.bindings) {
        b.timeline?.tick(now);
        for (const e of b.elements) tickMixer(e.mixer, dt);
      }

      renderer.render(scene, camera);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  async stop(): Promise<void> {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.mindar) await this.mindar.stop();
    for (const b of this.bindings) {
      for (const e of b.elements) e.dispose();
    }
    this.bindings = [];
  }
}
